import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #755 (Leland 2026-10-23) — the ⓘ on a job FAV 2.0 couldn't schedule (no crew, no date).
// Click → a popup with FAV 2.0's own reasons, the same text as the Inbox's "Why?", so the
// note doesn't get lost. The tile around it carries the red box (JobTile).
//
// Inline colours: the custom palette's `red` (#FF8080) is too light to read as a warning,
// and Tailwind's gray classes don't exist in this palette (FavInboxPanel ActionsMenu note).
// Every pointer event stops here so a click never opens the job or starts a drag.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const FAV_SKIP_RED = "#dc2626";
const POPUP_WIDTH = 380;

const stop = (e) => e.stopPropagation();

/**
 * Where the popup goes: under the button, or above it when there isn't room below (a tile near
 * the bottom of the window), kept inside the window. Anchored by `bottom` when above, so its
 * real height never matters. Pure, for the tests.
 */
export function favSkipPopupPosition(btn, view, width = POPUP_WIDTH, margin = 8, room = 240) {
  const left = Math.max(margin, Math.min(btn.left, view.width - margin - width));
  if (view.height - btn.bottom >= room || btn.top < view.height - btn.bottom) {
    return { top: btn.bottom + 4, left, maxHeight: Math.max(120, view.height - btn.bottom - 4 - margin) };
  }
  return { bottom: view.height - btn.top + 4, left, maxHeight: Math.max(120, btn.top - 4 - margin) };
}

const FavSkipInfo = ({ skip, className = "" }) => {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const firstLine = String(skip.why || "").split("\n")[0].trim();

  useEffect(() => {
    if (!pos) return undefined;
    const close = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setPos(null);
    };
    const onKey = (e) => e.key === "Escape" && setPos(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
    };
  }, [pos]);

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (pos) return setPos(null);
    const r = btnRef.current.getBoundingClientRect();
    setPos(favSkipPopupPosition(r, { width: window.innerWidth, height: window.innerHeight }));
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        draggable={false}
        data-testid="fav-skip-info"
        aria-expanded={!!pos}
        title={firstLine}
        onClick={toggle}
        onMouseDown={stop}
        onPointerDown={stop}
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className={`absolute top-0.5 right-0.5 w-[18px] h-[18px] rounded-full flex items-center justify-center text-[11px] font-bold italic leading-none ${className}`}
        style={{ background: FAV_SKIP_RED, color: "#ffffff", fontFamily: "Georgia, serif" }}
      >
        i
      </button>
      {pos &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            data-testid="fav-skip-why"
            onClick={stop}
            onMouseDown={stop}
            className="text-xs p-3 rounded-sm shadow-lg whitespace-pre-line"
            style={{
              position: "fixed",
              top: pos.top,
              bottom: pos.bottom,
              left: pos.left,
              width: POPUP_WIDTH,
              maxHeight: pos.maxHeight,
              overflowY: "auto",
              zIndex: 9999,
              backgroundColor: "rgb(var(--color-base))", // as RecommendedFillersPopover
              color: "rgb(var(--color-text-primary))",
              border: `2px solid ${FAV_SKIP_RED}`,
            }}
          >
            {skip.why}
          </div>,
          document.body
        )}
    </>
  );
};

export default FavSkipInfo;
